import dayjs from "dayjs";
import type { AlignValue, DashStyleValue, Point } from "highcharts";
import ReactDOMServer from "react-dom/server";

import { colorsPalettes } from "@similarweb/styles";
import type { ColumnDefinition } from "@similarweb/ui-components";
import { ChartTooltip } from "@similarweb/ui-components";
import { shortMonthAndShortYear } from "@similarweb/pro-logic-components";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, minimumValueChangeFilter, percentageSignFilter } from "app/filters/ngFilters";
import { BasicDurations } from "app/services/DurationService";

import {
    TITLE_CHANGE,
    TITLE_GROUP_SHARE,
    TITLE_KEYWORDS,
    TITLE_POSITION,
    TITLE_TOTAL,
} from "./RankingDistributionGraphConsts";
import type {
    IChangeType,
    IChartTooltipDataItem,
    IPoint,
    IRankingDistributionGraphTooltip,
} from "./RankingDistributionGraphTypes";
import { xAxisFormatter, yAxisFormatter } from "./RankingDistributionGraphUtils";

const RankingDistributionGraphTooltip: React.FC<IRankingDistributionGraphTooltip> = ({ points, data }) => {
    const tooltipData = Object.entries(data).find(([key, _]) => dayjs.utc(key).valueOf() === points[0].date)?.[1];
    const chartTooltipData: IChartTooltipDataItem[] = points.map((point) => {
        const { id, color, name, value } = point;
        const { keywordCount, share, change, totalKeywords } = tooltipData?.[id] || {
            keywordCount: 0,
            share: 0,
            change: null,
            totalKeywords: 0,
        };
        return {
            id,
            color,
            label: {
                label: name,
                background: color,
            },
            value,
            keywords: abbrNumberFilter()(keywordCount) as string,
            share: percentageSignFilter()(share, 1) as string,
            change: {
                value: change ? minimumValueChangeFilter()(change, 2) : "-",
                isDecrease: change ? change < 0 : undefined,
                unsigned: !change,
            },
            totalKeywords: abbrNumberFilter()(totalKeywords) as string,
        };
    });
    return (
        <ChartTooltip
            width={388}
            title={dayjs(points[0].date).utc().format(shortMonthAndShortYear)}
            columnDefinition={COLUMN_DEFINITION}
            data={chartTooltipData}
        >
            <ChartTooltip.TooltipFooterWithValue
                label={<ChartTooltip.LabelCell label={translate(TITLE_TOTAL)} />}
                value={chartTooltipData.length > 0 ? chartTooltipData[0].totalKeywords : ""}
            />
        </ChartTooltip>
    );
};

export const graphConfig = (data) => ({
    chart: {
        height: 237,
        marginTop: 0,
        marginBottom: 0,
        marginRight: 24,
    },
    plotOptions: {
        column: {
            borderWidth: 0,
            // pointWidth: 45,
            // groupPadding: 5
        },
    },

    xAxis: {
        labels: {
            formatter: function ({ value }) {
                return xAxisFormatter({ value });
            },
        },
        plotLines: [
            {
                color: colorsPalettes.carbon[200],
                width: 1,
                dashStyle: "ShortDash" as DashStyleValue,
                value: dayjs.utc().subtract(Number(BasicDurations.LAST_TWENTY_EIGHT_DAYS), "days").valueOf(),
                zIndex: 100,
                label: {
                    get text() {
                        return translate("keywordanalysis.organic2.clicks.over.time.graph.plotLine.last28");
                    },
                    align: "left" as AlignValue,
                    y: 20,
                    rotation: 0,
                    style: { color: colorsPalettes.carbon[200] },
                },
            },
        ],
    },
    yAxis: {
        showFirstLabel: true,
        reversedStacks: false,
        startOnTick: true,
        minPadding: 0,
        labels: {
            formatter: function ({ value }) {
                return yAxisFormatter({ value });
            },
        },
    },
    tooltip: {
        formatter(this: Point) {
            const points = this.points?.map((point) => {
                return {
                    id: point.series.userOptions.id,
                    color: point.color,
                    name: point.series.name,
                    value: point.y,
                    date: point.x,
                };
            });
            // eslint-disable-next-line import/no-named-as-default-member
            return ReactDOMServer.renderToString(
                <RankingDistributionGraphTooltip
                    points={points as IPoint[]}
                    data={data}
                />,
            );
        },
    },
});

interface TooltipRow {
    label: { label: string; background: string };
    keywords: string;
    share: string;
    change: IChangeType;
}

const COLUMN_DEFINITION: Array<ColumnDefinition<TooltipRow>> = [
    {
        key: "1",
        get title() {
            return translate(TITLE_POSITION);
        },
        selector: "label" as const,
        align: "left",
        size: ChartTooltip.LabelCellDefaultWidth,
        render: ({ label }: { label: { label: string; background: string } }) => {
            return (
                <ChartTooltip.LabelCell
                    label={label.label}
                    backgroundColor={label.background}
                />
            );
        },
    },
    {
        key: "2",
        get title() {
            return translate(TITLE_KEYWORDS);
        },
        selector: "keywords" as const,
        align: "right",
        size: ChartTooltip.ValueCellDefaultwidth,

        render: ({ keywords }: { keywords: string }) => {
            return <ChartTooltip.ValueCell value={keywords} />;
        },
    },
    {
        key: "3",
        get title() {
            return translate(TITLE_GROUP_SHARE);
        },
        align: "right",
        size: "70px",
        selector: "share" as const,
        render: ({ share }: { share: string }) => {
            return <ChartTooltip.ValueCell value={share} />;
        },
    },
    {
        key: "4",
        get title() {
            return translate(TITLE_CHANGE);
        },
        selector: "change" as const,
        align: "right",
        size: "80px",
        render: ({ change }: { change: IChangeType }) => (
            <ChartTooltip.ChangeCell
                value={change.value}
                isDecrease={change.isDecrease}
                unsigned={change.unsigned}
            />
        ),
    },
];
